import { Injectable } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ILogger, LogContext } from './ilogger';
import { redactSensitiveData } from './log-redaction.util';

@Injectable()
export class AppInsightsLoggerService implements ILogger {
  private appInsights?: ApplicationInsights;

  initialize(connectionString: string): void {
    if (!connectionString || this.appInsights) {
      return;
    }

    try {
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString,
          enableAutoRouteTracking: true,
          disableExceptionTracking: true
        }
      });

      this.appInsights.loadAppInsights();
    } catch {
      this.appInsights = undefined;
    }
  }

  debug(message: string, context?: LogContext): void {
    this.trackTrace('DEBUG', message, context);
  }

  info(message: string, context?: LogContext): void {
    this.trackTrace('INFO', message, context);
  }

  warn(message: string, context?: LogContext): void {
    this.trackTrace('WARN', message, context);
  }

  error(message: string, context?: LogContext): void {
    this.trackTrace('ERROR', message, context);
  }

  exception(error: unknown, context?: LogContext): void {
    try {
      if (!this.appInsights) {
        return;
      }

      const errorObject = error instanceof Error ? error : new Error(typeof error === 'string' ? error : 'Unknown exception');
      this.appInsights.trackException({
        exception: errorObject,
        properties: redactSensitiveData(context ?? {})
      });
    } catch {
      // Fail-safe by design.
    }
  }

  private trackTrace(severity: string, message: string, context?: LogContext): void {
    try {
      if (!this.appInsights) {
        return;
      }

      this.appInsights.trackTrace({
        message,
        severityLevel: this.toSeverityLevel(severity),
        properties: redactSensitiveData({
          ...context,
          severity
        })
      });
    } catch {
      // Fail-safe by design.
    }
  }

  private toSeverityLevel(level: string): number {
    switch (level) {
      case 'DEBUG':
        return 0;
      case 'INFO':
        return 1;
      case 'WARN':
        return 2;
      case 'ERROR':
        return 3;
      default:
        return 1;
    }
  }
}
